import firebase from 'firebase/compat/app'
import { getDecodedJWT } from './jwt_helper'
import { RouterRegistry } from './routerRegistry'
import store from '../store'
import { OauthService, LogsService } from '../services'

export const ensureActiveFirebaseAuthState = async (toLocationId: string) => {
  const router = RouterRegistry.getRouter()
  let currentUserFirebaseToken = await firebase?.auth()?.currentUser?.getIdToken()

  // If we have firebase token, we can check if the user has access to the location
  if (currentUserFirebaseToken) {
    const decodedPayload = getDecodedJWT(currentUserFirebaseToken)
    if (toLocationId && !decodedPayload?.locations?.includes(toLocationId)) {
      console.log(`This location: ${toLocationId} is not accessible. Redirecting to the dashboard page.`)
      router.push({ name: 'dashboard' })
      return
    }
  } else if (!currentUserFirebaseToken) {
    const auth = await store.dispatch('auth/get')
    // If we don't have firebase token, check if we have custom token in store
    if (auth?.firebaseToken) {
      try {
        // Decode the auth custom token and check if it is expired
        const decodedToken = getDecodedJWT(auth.firebaseToken)
        const isCustomTokenExpired = decodedToken?.exp && decodedToken?.exp < Date.now() / 1000
        if (isCustomTokenExpired) {
          // If custom token is expired, we can try and fetch new custom token from the server
          const response = await OauthService.getCurrentUser()
          await firebase.auth().signInWithCustomToken(response.firebaseToken)
          currentUserFirebaseToken = await firebase?.auth()?.currentUser?.getIdToken()

          // update the auth token in store
          await store.dispatch('auth/set', {
            ...auth,
            firebaseToken: response.firebaseToken
          })
        } else {
          // If custom token is not expired, we can use it
          await firebase.auth().signInWithCustomToken(auth.firebaseToken)
          currentUserFirebaseToken = await firebase?.auth()?.currentUser?.getIdToken()
        }
      } catch (e) {
        console.error('Error while signing in with custom token, attempt was from store firebase token', e)
      }
    }

    // If firebase token is still not present, we can try and fetch new custom token from the server
    if (!currentUserFirebaseToken && auth?.authToken) {
      try {
        const response = await OauthService.getCurrentUser()
        await firebase.auth().signInWithCustomToken(response.firebaseToken)
        currentUserFirebaseToken = await firebase?.auth()?.currentUser?.getIdToken()

        // update the auth token in store
        await store.dispatch('auth/set', {
          ...auth,
          firebaseToken: response.firebaseToken
        })
      } catch (e) {
        console.error('Error while signing in with custom token, attempt was from auth token', e)
      }
    }
    
    // if current token is still not present, we need to logout the user
    if (!currentUserFirebaseToken) {
      // if we have auth token, we can log to ES
      if (auth?.authToken) {
        try {
          await LogsService.dump('frontend-logout', {
            instance: 'Unable to get firebase token',
            origin: 'Bootstrap',
            page: window?.location?.href,
            route: {
              name: router.currentRoute.value.name,
              fullPath: router.currentRoute.value.fullPath
            }
          })
        } catch (err) {
          console.error('Error while logging to ES, reference - Unable to get firebase token', err)
        }
      }
      console.warn('Auth store inconsistent: No firebase token, logging out')
      await store.dispatch('logout')
    }
  }
};